// src/sync/SyncProvider.jsx
// Offline mode for this device and its syncing with the server (see sync.js). `useSync()` tells the rest of the
// app what state that is in.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { subscribeToAuth } from '../auth';
import { useConfirm } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { forgetGuest, guestDeclined, guestId, setGuestDeclined } from '../guest';
import { keepEngine } from '../engine/engineCache';
import { localEngineEnabled, offlineModeOn, setLocalEngine } from '../engine/engineFlag';
import { notifyDataChanged } from '../testMode/testMode';
import { localClient, resolveConflict, resolveMerge, runSync, serverClient } from './sync';

const SyncContext = createContext({ enabled: false });
export const useSync = () => useContext(SyncContext);

const PERIOD = 60 * 1000; // how often to look for changes on the server
const RETRY = 60 * 1000; // how often to try turning offline mode on again when that did not work
const DEBOUNCE = 2500; // how long after the last change on the device to send it

// phase: 'idle' (in step) | 'syncing' | 'pending' (changes not on the server yet) | 'offline' | 'conflict' | 'error'
export function SyncProvider({ children, autoSync = true }) {
    const [user, setUser] = useState(null);
    const [enabled, setEnabled] = useState(offlineModeOn());
    const [phase, setPhase] = useState(offlineModeOn() ? 'syncing' : 'idle'); // (until the first look at the server)
    const [message, setMessage] = useState(null);
    const [questions, setQuestions] = useState(null); // conflicts of a merge that need an answer
    const [lastSynced, setLastSynced] = useState(null);
    const [enabling, setEnabling] = useState(false);
    const running = useRef(false);
    const again = useRef(false);
    const enablingRef = useRef(false);
    const isGuest = Boolean(user && user.isGuest);
    const userId = user && !isGuest ? user.uid : null; // (a guest has nobody to sync with)
    const confirm = useConfirm();
    const toast = useToast();
    const askedAboutGuest = useRef(false);
    const syncNowRef = useRef(null);

    useEffect(() => subscribeToAuth(setUser), []);

    // Somebody who made tournaments as a guest on this device and now has an account is offered to take them along.
    useEffect(() => {
        if (!userId || askedAboutGuest.current || !guestId() || guestDeclined()) return;
        askedAboutGuest.current = true;
        (async () => {
            const guest = guestId();
            try {
                const made = await localClient(guest)('GET', '/getTournaments');
                if (made.length === 0) {
                    await localClient(guest)('POST', '/localSync/forget');
                    forgetGuest();
                    return;
                }
                const go = await confirm({
                    title: 'Add your guest tournaments to your account?',
                    message:
                        `You made ${made.length} tournament${made.length === 1 ? '' : 's'} as a guest on this device ` +
                        `(${made.map((t) => t.name).slice(0, 3).join(', ')}${made.length > 3 ? ', …' : ''}). ` +
                        'They are added to your account next to what is there; nothing of yours changes.',
                    confirmLabel: 'Add to my account',
                    danger: false,
                });
                if (!go) {
                    // Said no: never asked again. To be sure, they are asked once whether to delete the tournaments instead.
                    const remove = await confirm({
                        title: 'Delete the guest tournaments instead?',
                        message: 'They are not added to your account. Delete them from this device? This cannot be undone. (Cancel keeps them on this device, and you are not asked again.)',
                        confirmLabel: 'Delete them',
                    });
                    if (remove) {
                        await localClient(guest)('POST', '/localSync/forget');
                        forgetGuest();
                        toast.success('The guest tournaments were deleted from this device.');
                    } else {
                        setGuestDeclined(true);
                    }
                    return;
                }
                const mine = await localClient(guest)('GET', '/sync');
                const answer = await serverClient(userId)('POST', '/adopt', { data: mine.data });
                await localClient(guest)('POST', '/localSync/forget');
                forgetGuest();
                toast.success(`${answer.adopted} tournament${answer.adopted === 1 ? '' : 's'} added to your account.`);
                if (localEngineEnabled()) syncNowRef.current?.();
                else notifyDataChanged();
            } catch (error) {
                askedAboutGuest.current = false;
                toast.error('Could not add the guest tournaments to your account. They stay on this device; try again later.');
            }
        })();
    }, [userId, confirm, toast]);

    const finish = useCallback((result) => {
        setMessage(result.message || null);
        setQuestions(result.outcome === 'conflict' && result.conflicts ? result.conflicts : null);
        if (result.outcome === 'merged') notifyDataChanged();
        if (result.outcome === 'conflict') setPhase('conflict');
        else if (result.outcome === 'offline') setPhase('pending');
        else if (result.outcome === 'error') setPhase('error');
        else {
            if (result.outcome === 'pulled') notifyDataChanged(); // what is on screen is from before
            setPhase('idle');
            setLastSynced(new Date());
        }
    }, []);

    const syncNow = useCallback(async () => {
        if (!userId) return null;
        if (running.current) {
            again.current = true; // changes came in meanwhile: go round once more afterwards
            return null;
        }
        running.current = true;
        setPhase((current) => (current === 'conflict' ? current : 'syncing'));
        let result;
        let takenFromServer = false;
        try {
            do {
                again.current = false;
                result = await runSync(userId);
                takenFromServer = takenFromServer || result.outcome === 'pulled' || result.outcome === 'merged';
            } while (again.current && result.outcome !== 'conflict' && result.outcome !== 'offline');
        } finally {
            running.current = false;
        }
        finish(result);
        if (takenFromServer) notifyDataChanged(); // (a later round of the loop may have been quiet: what is shown is still old)
        return result;
    }, [userId, finish]);

    // when to look: at the start, when the connection or the app comes back, now and then, and after a change here
    useEffect(() => {
        if (!enabled || !userId || !autoSync) return undefined;
        let timer = null;
        const soon = () => {
            clearTimeout(timer);
            timer = setTimeout(syncNow, DEBOUNCE);
        };
        const onVisible = () => document.visibilityState === 'visible' && syncNow();
        syncNow();
        const interval = setInterval(syncNow, PERIOD);
        window.addEventListener('online', syncNow);
        document.addEventListener('visibilitychange', onVisible);
        let stopWrites = () => {};
        import('../engine/localEngine').then((engine) => {
            stopWrites = engine.subscribeToLocalWrites(() => {
                if (!running.current) {
                    setPhase((current) => (current === 'conflict' ? current : 'pending'));
                    soon();
                }
            });
        });
        return () => {
            clearTimeout(timer);
            clearInterval(interval);
            window.removeEventListener('online', syncNow);
            document.removeEventListener('visibilitychange', onVisible);
            stopWrites();
        };
    }, [enabled, userId, autoSync, syncNow]);

    // Turns offline mode on for this device: keep the storage, get the engine, bring the data over, then switch.
    const enable = useCallback(async () => {
        if (!userId || enablingRef.current) return { outcome: 'error', message: 'Sign in first.' };
        enablingRef.current = true;
        setEnabling(true);
        try {
            if (navigator.storage && navigator.storage.persist) await navigator.storage.persist().catch(() => false);
            const engine = await import('../engine/localEngine');
            await engine.whenEngineReady();
            if (!(await keepEngine())) throw new Error('The offline engine could not be saved on this device.');
            const result = await runSync(userId);
            if (['idle', 'pulled', 'pushed', 'merged', 'conflict'].includes(result.outcome)) {
                setLocalEngine(true);
                setEnabled(true);
            }
            finish(result);
            return result;
        } catch (error) {
            const result = { outcome: 'error', message: error.message };
            finish(result);
            return result;
        } finally {
            enablingRef.current = false;
            setEnabling(false);
        }
    }, [userId, finish]);

    // A guest works on the device from the start: make sure the engine is also kept for starting without a connection.
    useEffect(() => {
        if (!isGuest || !autoSync) return undefined;
        let stopped = false;
        const keep = async () => {
            if (!stopped && navigator.onLine !== false && !(await keepEngine())) setTimeout(keep, RETRY);
        };
        keep();
        window.addEventListener('online', keep);
        return () => {
            stopped = true;
            window.removeEventListener('online', keep);
        };
    }, [isGuest, autoSync]);

    // Offline mode switches itself on for every signed-in device: nobody has to ask for it. If the engine cannot be
    // fetched now (no connection, a hiccup), it is tried again when the connection returns and every minute.
    useEffect(() => {
        if (!autoSync || !userId || enabled) return undefined;
        let timer = null;
        let stopped = false;
        const attempt = async () => {
            clearTimeout(timer);
            if (stopped || enablingRef.current) return;
            const result = navigator.onLine === false ? { outcome: 'offline' } : await enable();
            if (!stopped && !['idle', 'pulled', 'pushed', 'merged', 'conflict'].includes(result.outcome)) {
                timer = setTimeout(attempt, RETRY);
            }
        };
        attempt();
        window.addEventListener('online', attempt);
        return () => {
            stopped = true;
            clearTimeout(timer);
            window.removeEventListener('online', attempt);
        };
    }, [autoSync, userId, enabled, enable]);

    const resolve = useCallback(
        async (choice) => {
            if (!userId) return null;
            running.current = true;
            setPhase('syncing');
            let result;
            try {
                result = await resolveConflict(userId, choice);
            } finally {
                running.current = false;
            }
            finish(result);
            return result;
        },
        [userId, finish]
    );

    const answer = useCallback(
        async (choices) => {
            if (!userId) return null;
            running.current = true;
            setPhase('syncing');
            let result;
            try {
                result = await resolveMerge(userId, choices);
            } finally {
                running.current = false;
            }
            finish(result);
            return result;
        },
        [userId, finish]
    );

    const disable = useCallback(() => {
        setLocalEngine(false);
        setEnabled(false);
        setPhase('idle');
    }, []);

    syncNowRef.current = syncNow;
    const value = { enabled, enabling, phase, message, lastSynced, questions, isGuest, signedIn: Boolean(userId), syncNow, enable, resolve, answer, disable };
    return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
